'use client';

import { useEffect, useState, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { tokenValidationService } from '../services/tokenValidationService';
import { performSignOut } from '../utils/signOut';
import toast from 'react-hot-toast';

interface TokenExpiryConfig {
  warningTime: number; // seconds before expiry to show warning
  checkInterval: number; // milliseconds between checks
}

// Pages where token expiry monitoring should not run
const EXCLUDED_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/temporary-login',
  '/temporary-login',
  '/auth/forgot-password',
  '/auth/signout',
  '/auth/error',
  '/auth/qr',
];

export const useTokenExpiry = (
  config: TokenExpiryConfig = { warningTime: 60, checkInterval: 60000 }
) => {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleSignOut = useCallback(async () => {
    const isTemporaryUser = session?.isTemporaryUser;
    const message = isTemporaryUser
      ? 'Your temporary access has expired. You have been signed out.'
      : 'Your session has expired. You have been signed out.';
    toast.error(message, {
      duration: 5000,
    });
    // Use the proper sign-out utility
    await delay(10000); // wait 5 seconds
    await performSignOut('/auth/login');
  }, [session?.isTemporaryUser]);

  useEffect(() => {
    // Don't run token expiry monitoring on auth pages or if not authenticated
    if (
      status !== 'authenticated' ||
      !session?.accessToken ||
      EXCLUDED_PATHS.includes(pathname)
    ) {
      return;
    }

    const checkTokenExpiry = async () => {
      if (!session?.accessToken) return;

      // Don't show warning if page is not visible (user is not actively using the app)
      if (document.hidden) return;

      try {
        // For temporary users, check with backend first
        if (session.isTemporaryUser) {
          const backendValidation =
            await tokenValidationService.checkSessionValidity(session);
          if (!backendValidation.isValid) {
            // Token is invalid on backend, sign out immediately
            handleSignOut();
            return;
          }
        }

        // Decode JWT token to get expiry
        const tokenParts = session.accessToken.split('.');
        if (tokenParts.length !== 3) return;

        const payload = JSON.parse(atob(tokenParts[1]));
        const expiryTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiry = Math.floor((expiryTime - currentTime) / 1000); // Convert to seconds

        // For temporary users, check more frequently and show warning earlier
        const isTemporaryUser = session.isTemporaryUser;
        const warningTime = isTemporaryUser
          ? Math.min(config.warningTime, 60)
          : config.warningTime;

        if (timeUntilExpiry <= 0) {
          // Token has expired, but don't sign out - let refresh mechanism handle it
          // The useTokenRefresh hook and API interceptor will automatically refresh
          return;
        }

        if (timeUntilExpiry <= warningTime && !showExpiryModal) {
          // Show warning modal
          setTimeRemaining(timeUntilExpiry);
          setShowExpiryModal(true);
        }
      } catch (error) {
        console.error('Error checking token expiry:', error);
      }
    };

    // Initial check
    checkTokenExpiry();

    // Set up interval for periodic checks
    const interval = setInterval(checkTokenExpiry, config.checkInterval);

    return () => clearInterval(interval);
  }, [
    status,
    session?.accessToken,
    session?.isTemporaryUser,
    pathname,
    config.warningTime,
    config.checkInterval,
    showExpiryModal,
    handleSignOut,
  ]);

  const closeExpiryModal = useCallback(() => {
    setShowExpiryModal(false);
  }, []);

  return {
    showExpiryModal,
    timeRemaining,
    closeExpiryModal,
    handleSignOut,
  };
};
